/**
 * The ad group's goal, bid and placements as the wizard asks for them — the screen's half of
 * `apps/worker/src/platform/line-item-fields.ts`.
 *
 * 🔴 The lists here are the worker's, copied, and `line-item-fields.test.ts` reads the worker's
 * file to hold them equal: a value offered here and not there is a request the worker refuses
 * as «not offered (not measured)», and a value there and not here is a choice nobody can make.
 * The reasons for each list live in the worker's module, beside the lines of X's reference
 * they come from; this file only decides what the screen may send and what it asks for first.
 *
 * Used by the wizard (lib/create-ad.ts, pages/CreateAd.tsx) since PR 5's wiring
 * (audits/2026-10-23/pr45-spec.md).
 */
import type { LineItemObjective } from "./api.ts";

/** Goals the worker offers per objective — copied from its `LINE_ITEM_GOALS`. */
export const GOALS: Readonly<Record<LineItemObjective, readonly string[]>> = {
	ENGAGEMENTS: [],
	WEBSITE_CLICKS: ["SITE_VISITS", "LINK_CLICKS"],
};

export type BidStrategy = "AUTO" | "MAX" | "TARGET";

/** Strategies the worker offers per objective — copied from its `LINE_ITEM_BID_STRATEGIES`. */
export const BID_STRATEGIES: Readonly<Record<LineItemObjective, readonly BidStrategy[]>> = {
	ENGAGEMENTS: ["AUTO", "MAX"],
	WEBSITE_CLICKS: ["AUTO", "MAX", "TARGET"],
};

export type Placement =
	| "ALL_ON_TWITTER"
	| "TWITTER_TIMELINE"
	| "TWITTER_PROFILE"
	| "TWITTER_SEARCH";

/** Placements the worker offers — copied from its `LINE_ITEM_PLACEMENTS`, in its order. */
export const PLACEMENTS: readonly Placement[] = [
	"ALL_ON_TWITTER",
	"TWITTER_TIMELINE",
	"TWITTER_PROFILE",
	"TWITTER_SEARCH",
];

/** Whether a strategy needs an amount: «Required if bid_strategy is set to either MAX or TARGET» (R:3051). */
export function bidNeedsAmount(strategy: BidStrategy): boolean {
	return strategy !== "AUTO";
}

export type PlacementProblem =
	| "placements-empty"
	| "placements-all-alone"
	| "placements-profile-alone";

/**
 * The placements' own problem, in the worker's order: none chosen, ALL_ON_TWITTER beside
 * another (the console's choice), TWITTER_PROFILE alone (X's rule). `null` means they may
 * be sent.
 */
export function placementsProblem(chosen: readonly Placement[]): PlacementProblem | null {
	if (chosen.length === 0) return "placements-empty";
	if (chosen.includes("ALL_ON_TWITTER") && chosen.length > 1) return "placements-all-alone";
	if (chosen.length === 1 && chosen[0] === "TWITTER_PROFILE") return "placements-profile-alone";
	return null;
}

/**
 * 🔴 «`TWITTER_SEARCH` requires keyword targeting» (G:883) — asked of the audience step, not
 * of the ad group's. The placements are chosen before the keywords in the wizard, and the
 * worker cannot see both in one body (the line item is created before its criteria), so the
 * screen that holds the whole draft is the only place this condition can be checked at all.
 */
export function searchWithoutKeywords(chosen: readonly Placement[], keywords: number): boolean {
	return chosen.includes("TWITTER_SEARCH") && keywords === 0;
}

/**
 * The strategy kept when the objective changes: a strategy the new objective does not
 * offer falls back to AUTO rather than being sent and refused (TARGET under ENGAGEMENTS).
 */
export function strategyFor(objective: LineItemObjective, current: BidStrategy): BidStrategy {
	return BID_STRATEGIES[objective].includes(current) ? current : "AUTO";
}

/** The goal kept when the objective changes: one the new objective does not offer becomes «not sent». */
export function goalFor(objective: LineItemObjective, current: string): string {
	return GOALS[objective].includes(current) ? current : "";
}
